/**
 * Every program the descent will draw with, linked ahead of the frame that
 * would otherwise pay for it: the plot's three shaders, the tile material's
 * eight variants, the scatter's own and the buildings' three, built off degenerate triangles and
 * queued through `warmPrograms` so no one frame pays for more than one
 * program's link.
 *
 * Reads the closure it was cut out of through `ShaderWarmDeps`. `dojoPaints`
 * and `plotBuild` are built below this seam's own call site in atlas.ts,
 * so a plain reference there would read them before they exist.
 */

import * as THREE from 'three';

import { PLOT_CSM_GLSL, warmPlotPrograms } from './dtmPlot.ts';
import { warmStructurePrograms } from './structureMeshes.ts';
import type { WarmProgramHandle } from './tileMesh';
import { warmTilePrograms } from './tileMesh.ts';
import { warmRockPrograms } from './rockField.ts';
import type { CascadesHandle } from './cascades';
import type { PyramidViewHandle } from './pyramidView';
import type { MountHandle } from './mount';
import type { BodyReadersHandle } from './bodyReaders';
import type { AtmosphereViewHandle } from './atmosphereView';
import type { BodySceneHandle } from './bodyScene';
import type { DojoPaintsHandle } from './dojoPaints';
import type { PlotBuildHandle } from './plotBuild';
import { TEST_SWITCHES } from '../urlFlags.ts';

export interface ShaderWarmDeps {
  readonly renderer: THREE.WebGLRenderer;
  readonly cascades: CascadesHandle;
  readonly pyramidView: PyramidViewHandle;
  own(fn: () => void): void;
  readonly mount: MountHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly atmosphereView: AtmosphereViewHandle;
  readonly bodyScene: BodySceneHandle;
  /** Built below this seam's own call site, so getters defer the read past
   *  construction time instead of tripping its TDZ. */
  readonly dojoPaints: DojoPaintsHandle;
  readonly plotBuild: PlotBuildHandle;
}

export interface ShaderWarmHandle {
  linkInLeg: boolean;
  rockLinkInLeg: boolean;
  warmDue: boolean;
  warmQuiet: number;
  warmDrawing: boolean;
  readonly warmBodyId: string | null;
  readonly warmCensus: string;
  readonly warmRuns: number;
  readonly warmMaxMs: number;
  readonly warmLastMs: number;
  readonly warmTiles: WarmProgramHandle | null;
  readonly warmPlot: { readonly group: THREE.Group; dispose(): void } | null;
  readonly warmRocks: { readonly group: THREE.Group; dispose(): void } | null;
  readonly warmStructures: { readonly group: THREE.Group; dispose(): void } | null;
  /** True once the descent can be ridden with nothing left to link. */
  warmSettled(): boolean;
  warmDescentPrograms(): void;
  /** The scene's own lights, as three counts them into a program's cache key. */
  lightCensus(): string;
}

export function createShaderWarm(deps: ShaderWarmDeps): ShaderWarmHandle {
  const { renderer, cascades, pyramidView, own } = deps;
  const { atlasRoot, scene, camera } = deps.mount;
  const { artificialDebug, rowById, ladderBody } = deps.bodyReaders;
  const { tileAtmoRef, plotAtmoRef } = deps.atmosphereView;

  /**
   * `--ship --inject link-in-leg`: the pre-compile skipped.
   *
   * The control for this whole mechanism. With it armed nothing is linked at
   * mount, so the plot's three shaders and the programs the cascade lights
   * rekey land back on the frames the descent's own legs draw - which is what
   * the 50 ms gate on REGIONAL->LOCAL and PLOT->SURFACE has to go red on.
   *
   * Read off `?linkinleg=1` and not only through `setLinkDebug`: the mount is
   * over before a harness can reach into the page, so a switch thrown from
   * outside arms after the frames it is about have been paid.
   */
  let linkInLeg = typeof location !== 'undefined' && TEST_SWITCHES
    && new URLSearchParams(location.search).get('linkinleg') === '1';
  /**
   * `--ship --inject rock-link-in-leg`: the scatter left out of the pre-compile.
   *
   * The narrow half of the control above. The rest of the warm runs, so what
   * the PLOT to SURFACE leg then links is the scatter's own two programs and
   * nothing else. On the URL for the reason `linkinleg` is.
   */
  let rockLinkInLeg = typeof location !== 'undefined' && TEST_SWITCHES
    && new URLSearchParams(location.search).get('rocklinkinleg') === '1';
  /**
   * `--ship --inject sync-compile`: today's one-frame link, restored.
   *
   * The control: `linkinleg` skips the pre-compile so the legs
   * pay for it instead; this flag keeps the pre-compile at mount but forces
   * it back into the single blocking `renderer.compile(scene, camera)` this
   * lane replaced with the warmQueue, so the black-screen frame at the LAND
   * press has a switch that reproduces it on demand.
   */
  /**
   * `structure-visuals-check --inject no-warm`: the buildings left out of the
   * warm, so the first one placed links its three programs on the frame it
   * appears. On the URL for the reason `linkinleg` is.
   */
  const structureLinkLate = typeof location !== 'undefined' && TEST_SWITCHES
    && new URLSearchParams(location.search).get('nostructwarm') === '1';
  let syncCompile = typeof location !== 'undefined' && TEST_SWITCHES
    && new URLSearchParams(location.search).get('synccompile') === '1';
  // At MOUNT, and not at the first SURFACE tick they light. Three counts
  // shadow-casting lights into `shadowMapEnabled` and `numDirLightShadows`,
  // both inputs to every non-raw material's program cache key, so anything
  // linked before they exist - the orrery's own `linkPrograms`, the globe, the
  // rings, the star field - is linked a second time the moment the walkaround
  // arms them. That second link is six programs on one frame at the SURFACE
  // edge. They cost three 1024^2 depth targets for the visit, which the lazy
  // build here used to save on a session that never walked around, and nothing
  // is ever drawn into them until `updateShadowCascades` sets
  // `shadowMap.needsUpdate`. `link-in-leg` is the control: with the arming left
  // where it was, at the first SURFACE tick, six already-linked programs are
  // rekeyed on the frame the plot mounts.
  if (!linkInLeg) cascades.armCascades();

  /**
   * Every program the descent will draw with, linked once at Atlas mount.
   *
   * A GL link plus three's own uniform enumeration for it is 40 to 85 ms on
   * the calling thread, and staging cannot divide one link, so any program
   * first asked for on a band crossing puts two or three vsync frames into
   * that leg's 50 ms budget. The plot's three shaders, the tile material's
   * eight variants and the scatter's own are built here as prototypes off
   * degenerate triangles; everything else the Atlas already holds - the
   * orrery, the globe, the rings, the sky quad - is walked where it stands.
   * The scatter's prototype is an InstancedMesh that CASTS, because instancing
   * keys a program of its own and the shadow pass draws an instanced
   * MeshDepthMaterial that `renderer.compile` never reaches.
   *
   * Three things make the warmed program the program the descent then uses,
   * and each of them cost a round to find. `armCascades()` FIRST: three counts
   * shadow-casting lights into `shadowMapEnabled` and `numDirLightShadows`,
   * which are inputs to every non-raw material's cache key, so the globe, the
   * rings and the tiles used to relink the moment the walkaround armed the
   * cascades at the SURFACE edge - six programs on one frame. Inside
   * `stage.begin()`'s bracket: the renderer's own shadow-map state is that
   * same key's, and a compile from between frames links a program the draw
   * throws away. And `getUniforms()` on every program in the cache: three
   * defers the LINK_STATUS read and the GetActiveUniform/GetUniformLocation
   * round trips - 183 of them for one program - to the first draw, so a
   * compile alone leaves most of the cost where it was.
   */
  let warmDue = false;
  let warmBodyId: string | null = null;
  /**
   * How many warms in a row have linked nothing new.
   *
   * The warm cannot be a single shot on a chosen frame, because two of its
   * inputs settle on their own schedule: a body's air is mounted when the
   * registry lands, and the scene's light census still holds the cascades of
   * an Atlas that has not been torn down yet - and BOTH are inputs to every
   * program's cache key, so a warm taken too early links a variant nothing
   * ever draws. So it repeats until two frames running add no program, which
   * is the reading that says the census and the sources have stopped moving.
   * A repeat that finds everything cached is a graph walk and a map lookup.
   */
  let warmQuiet = 0;
  let warmCensus = '';
  let warmRuns = 0;
  let warmMaxMs = 0;
  let warmLastMs = 0;
  let warmDrawing = false;
  // True while the async path below has groups still in `warmQueue`: a second
  // call landing before the first settles must not rebuild or re-enqueue.
  let warmPending = false;
  let warmTiles: WarmProgramHandle | null = null;
  let warmPlot: { readonly group: THREE.Group; dispose(): void } | null = null;
  let warmRocks: { readonly group: THREE.Group; dispose(): void } | null = null;
  let warmStructures: { readonly group: THREE.Group; dispose(): void } | null = null;
  /**
   * Bumped by `releaseWarm()`; the `next()` chain below reads it to know its
   * own drain is stale.
   *
   * A body switch or full unmount mid-drain used to leave the
   * per-item promise chain running on disposed objects, each item's own
   * 4000ms deadline chaining into the next one for up to tens of seconds
   * after the player left. Every `next()` closure closes over the generation
   * it was built for and stops as soon as it no longer matches.
   */
  let warmGeneration = 0;
  /**
   * Children whose program has already linked, by object identity.
   *
   * A second descent to the same body (`warmBodyId` unchanged)
   * keeps the same prototype groups and so the same child objects, and used
   * to re-queue every one of them through `compileAsync` again - wasted
   * frames re-asking a program cache that already has the answer. Not
   * cleared on `releaseWarm()`: the groups it disposes take their children's
   * identities with them, so a new body's items can never collide with it.
   */
  const warmLinked = new WeakSet<THREE.Object3D>();

  function releaseWarm(): void {
    if (warmTiles) { atlasRoot.remove(warmTiles.group); warmTiles.dispose(); warmTiles = null; }
    if (warmPlot) { atlasRoot.remove(warmPlot.group); warmPlot.dispose(); warmPlot = null; }
    if (warmRocks) { atlasRoot.remove(warmRocks.group); warmRocks.dispose(); warmRocks = null; }
    if (warmStructures) { atlasRoot.remove(warmStructures.group); warmStructures.dispose(); warmStructures = null; }
    warmBodyId = null;
    warmGeneration++;
    deps.plotBuild.state.warmQueue = [];
    warmPending = false;
  }
  own(() => releaseWarm());

  /**
   * True once the descent can be ridden with nothing left to link.
   *
   * The two frames of quiet are not enough on their own: before the registry
   * lands there is no orrery to walk and no air to build a source from, so a
   * warm taken then links nothing and reports quiet on an empty scene. Both
   * are named here, and `descent-dojo` holds its first leg on this reading.
   */
  function warmSettled(): boolean {
    if (warmDue || warmQuiet < 2 || !deps.bodyScene.state.bodyNode) return false;
    const row = rowById(ladderBody());
    const wantsAir = row?.atmosphere.kind === 'data' && deps.dojoPaints.skyDebug.injectFrom === null;
    return !wantsAir || deps.atmosphereView.atmoBodyId === ladderBody();
  }

  function warmDescentPrograms(): void {
    // A second call landing while the async path still has groups in
    // `warmQueue` must not rebuild the groups or enqueue them twice; the
    // bracket keeps calling in every frame while `warmQuiet < 2`.
    if (warmPending) return;
    const t0 = performance.now();
    // The body AND its air: the plot's and the tile's fragment source carry the
    // scattering chunk, which is generated per body, so a prototype built
    // before the air was mounted is a different program from the one the
    // descent then asks for.
    const id = `${ladderBody()}|${deps.atmosphereView.atmoBodyId ?? 'none'}`;
    if (warmBodyId !== id) {
      releaseWarm();
      warmBodyId = id;
      warmTiles = warmTilePrograms(tileAtmoRef(), pyramidView.tileCoverage.ref,
        pyramidView.artificialLayerOn() ? pyramidView.tileArtificial.ref : null,
        artificialDebug.legacyTileFill ? null : deps.bodyScene.state.orrery?.byId(ladderBody())?.artificial ?? null,
        artificialDebug.reliefGlobeOnly ? null : deps.bodyScene.state.orrery?.byId(ladderBody())?.relief ?? null,
        pyramidView.artificialLayerOn() && deps.bodyScene.state.orrery?.byId(ladderBody())?.tileRelief
          ? pyramidView.tileReliefOn : null,
        deps.bodyScene.state.orrery?.byId(ladderBody())?.shadow ?? null);
      warmPlot = warmPlotPrograms(renderer, plotAtmoRef(), cascades.plotShadowRef());
      atlasRoot.add(warmTiles.group);
      atlasRoot.add(warmPlot.group);
      // Body-independent: the scatter's source carries no scattering chunk, so
      // it is rebuilt with the rest only to keep one handle and one release.
      if (!rockLinkInLeg) {
        warmRocks = warmRockPrograms();
        atlasRoot.add(warmRocks.group);
      }
      // Body-independent too; the cascade chunk is in the parts' source
      // exactly when the plot has a shadow ref to hand them.
      if (!structureLinkLate) {
        warmStructures = warmStructurePrograms(cascades.plotShadowRef() ? PLOT_CSM_GLSL : null);
        atlasRoot.add(warmStructures.group);
      }
    }
    cascades.armCascades();
    const before = renderer.info.programs?.length ?? 0;
    warmTiles!.group.visible = true;
    warmPlot!.group.visible = true;
    if (warmRocks) warmRocks.group.visible = true;
    if (warmStructures) warmStructures.group.visible = true;
    // `compile` gathers the scene's lights from what is VISIBLE, and the sky
    // quad spends the descent hidden.
    const skyWas = deps.atmosphereView.atmoSky ? deps.atmosphereView.atmoSky.mesh.visible : false;
    if (deps.atmosphereView.atmoSky) deps.atmosphereView.atmoSky.mesh.visible = true;

    // Shared tail, run once whichever path above has linked: restores the
    // sky's own visibility, arms the first-draw pass ANGLE needs, forces the
    // shadow variants `compile` never reaches, and settles `warmQuiet`.
    const finishWarm = (): void => {
      if (deps.atmosphereView.atmoSky) deps.atmosphereView.atmoSky.mesh.visible = skyWas;
      // Left VISIBLE for the render that follows this call, and hidden after
      // it. ANGLE defers the driver's own half of a link to the first DRAW of
      // the program, so a prototype that is compiled and never drawn moves
      // three's work to the mount and leaves the driver's wherever the first
      // real draw falls - which read as sporadic 300 to 800 ms frames on
      // whichever leg that was. The triangles have no area, so nothing is
      // rasterised.
      warmDrawing = true;
      // And one shadow pass: the three cascade maps are WRITTEN once here,
      // and every caster's depth variant is linked here with them - `compile`
      // walks the draw and not the shadow, so a caster armed at the SURFACE
      // edge used to link its depth program on the frame that armed it.
      // They are sampled by every lit material from the moment the lights
      // are armed, and a render target the driver has never drawn into is
      // lazily initialised on a frame of its own choosing: sporadic 280 to
      // 800 ms frames inside `renderer.render`, on whichever leg they landed
      // on, with nothing arriving and no program linked.
      renderer.shadowMap.needsUpdate = true;
      warmQuiet = (renderer.info.programs?.length ?? 0) === before ? warmQuiet + 1 : 0;
      warmCensus = lightCensus();
      warmRuns++;
      warmLastMs = performance.now() - t0;
      if (warmLastMs > warmMaxMs) warmMaxMs = warmLastMs;
      warmPending = false;
    };

    // The link status, the uniform locations and the attribute locations for
    // a span of the program cache - the part `compile`/`compileAsync` leave
    // for the first draw or first use, and 40 to 85 ms of blocking GL round
    // trips per program. Paid here, over the slice of the cache this call's
    // own compile just grew, so the cost lands beside the compile that earned
    // it rather than in one pass over everything at the end.
    const forceReady = (fromCount: number): void => {
      for (const prog of (renderer.info.programs ?? []).slice(fromCount)) {
        (prog as unknown as { getUniforms(): unknown }).getUniforms();
      }
    };

    if (syncCompile) {
      // `--inject sync-compile`: today's mechanism, one call for the whole
      // scene, on the calling thread, in this one frame.
      //
      // The whole scene, as ONE argument. Handed an object and a separate
      // target scene, three walks both for lights - and this object is
      // inside that scene, so every Atlas light was counted twice: the warm
      // linked programs keyed on 6 cascades and 2 suns and the draw then
      // linked its own, keyed on 3 and 1. The sim's own subtree is detached
      // by `subtree.activate()` above, so the walk is the Atlas's.
      renderer.compile(scene, camera);
      forceReady(before);
      finishWarm();
      return;
    }

    // The fix: hand the three prototype groups' own children to the same
    // `warmQueue` that `warmPrograms()` drains at one object per frame, so
    // the press pays a few frames of link instead of one. Each child is one
    // material's prototype mesh (tileMesh.ts's eight variants, dtmPlot.ts's
    // three, rockField.ts's one), so no one frame pays for more than one
    // program's own link - the whole group in one queue entry left the
    // tiles material's eight variants on one frame, still 300 to 800 ms
    // wide. Each item's own `getUniforms` pass runs as soon as ITS compile
    // lands, not batched with the rest.
    warmPending = true;
    const myGeneration = warmGeneration;
    const items: THREE.Object3D[] = [
      ...warmTiles!.group.children, ...warmPlot!.group.children,
      ...(warmRocks ? warmRocks.group.children : []),
      ...(warmStructures ? warmStructures.group.children : []),
    ];
    let i = 0;
    const next = (): void => {
      // A body switch or an Atlas unmount bumped `warmGeneration` and already
      // ran `releaseWarm()`; this closure's own body is gone, so it must not
      // touch `warmPending`/`warmQuiet` or reach into the disposed groups.
      if (myGeneration !== warmGeneration) return;
      if (i >= items.length) { finishWarm(); return; }
      const item = items[i]!;
      i++;
      if (warmLinked.has(item)) { next(); return; }
      const itemBefore = renderer.info.programs?.length ?? 0;
      void deps.plotBuild.warmPrograms(item).then(() => {
        if (myGeneration !== warmGeneration) return;
        forceReady(itemBefore);
        warmLinked.add(item);
        next();
      });
    };
    next();
  }

  /**
   * The scene's own lights, as three counts them into a program's cache key.
   *
   * Read at the warm and again where a link is not expected: a warm taken
   * under a census the descent does not draw under links a program nothing
   * uses, and the difference is invisible in the image and 80 ms in a leg.
   */
  function lightCensus(): string {
    let dir = 0; let point = 0; let other = 0; let shadow = 0;
    const roots: string[] = [];
    scene.traverseVisible((o) => {
      const l = o as THREE.Light;
      if (!l.isLight) return;
      if ((l as THREE.DirectionalLight).isDirectionalLight) dir++;
      else if ((l as THREE.PointLight).isPointLight) point++;
      else other++;
      if (l.castShadow) shadow++;
      let top: THREE.Object3D = o;
      while (top.parent && top.parent !== scene) top = top.parent;
      if (!roots.includes(top.name)) roots.push(top.name);
    });
    return `dir ${dir}, point ${point}, other ${other}, shadow ${shadow}; ${roots.join(' + ')}`;
  }

  return {
    get linkInLeg() { return linkInLeg; },
    set linkInLeg(v) { linkInLeg = v; },
    get rockLinkInLeg() { return rockLinkInLeg; },
    set rockLinkInLeg(v) { rockLinkInLeg = v; },
    get warmDue() { return warmDue; },
    set warmDue(v) { warmDue = v; },
    get warmQuiet() { return warmQuiet; },
    set warmQuiet(v) { warmQuiet = v; },
    get warmDrawing() { return warmDrawing; },
    set warmDrawing(v) { warmDrawing = v; },
    get warmBodyId() { return warmBodyId; },
    get warmCensus() { return warmCensus; },
    get warmRuns() { return warmRuns; },
    get warmMaxMs() { return warmMaxMs; },
    get warmLastMs() { return warmLastMs; },
    get warmTiles() { return warmTiles; },
    get warmPlot() { return warmPlot; },
    get warmRocks() { return warmRocks; },
    get warmStructures() { return warmStructures; },
    warmSettled,
    warmDescentPrograms,
    lightCensus,
  };
}
